import { useEffect, useState } from "react";
import { ArrowRight, Link2, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { errMsg, publicPost } from "@/lib/api";
import { SearchResults } from "@/components/search/SearchResults";
import { Seo } from "@/components/Seo";

const STEPS = ["Leggo la pagina del prodotto…", "Identifico marca e modello…", "Cerco nei negozi italiani…", "Confronto prezzi e spedizioni…"];
const KEY = "pgc_last_search";

export default function Home() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [result, setResult] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem(KEY));
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (!loading) return;
    const t = setInterval(() => setStep((s) => Math.min(s + 1, STEPS.length - 1)), 7000);
    return () => clearInterval(t);
  }, [loading]);

  const submit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setLoading(true);
    setStep(0);
    setResult(null);
    try {
      const r = await publicPost("/search", { url: url.trim() });
      setResult(r);
      sessionStorage.setItem(KEY, JSON.stringify(r));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-amber-500/10 blur-3xl" />
        <div className="pointer-events-none absolute -left-32 top-40 h-[380px] w-[380px] rounded-full bg-sky-500/10 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-4 pb-16 pt-14 sm:px-6 lg:grid-cols-[1.3fr_1fr] lg:pt-24">
          <Seo description="Incolla il link di un prodotto e trova il prezzo più basso nei negozi italiani. Oltre 6.000 prodotti di elettronica all'ingrosso, punti su ogni acquisto e status Bronze, Gold e Diamond." />
          <div className="fade-up">
            <p className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-amber-300">
              <Sparkles className="h-3.5 w-3.5" /> Il club che compra al prezzo giusto
            </p>
            <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Incolla un link.<br />
              <span className="bg-gradient-to-r from-amber-200 via-amber-400 to-amber-600 bg-clip-text text-transparent">Trova il prezzo migliore.</span>
            </h1>
            <p className="mt-5 max-w-xl text-base text-slate-400 md:text-lg">
              Cerchiamo lo stesso prodotto nei negozi italiani, ti mostriamo l'offerta più conveniente e puoi comprarla da solo o farla acquistare a noi. Ogni euro ti fa guadagnare punti.
            </p>
            <form onSubmit={submit} className="mt-8 flex flex-col gap-3 rounded-2xl border border-white/10 bg-[#12141C]/90 p-2 backdrop-blur sm:flex-row" data-testid="search-form">
              <div className="flex flex-1 items-center gap-3 px-3">
                <Link2 className="h-5 w-5 shrink-0 text-slate-500" />
                <input
                  data-testid="product-search-input"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://www.amazon.it/dp/…"
                  className="h-12 w-full bg-transparent text-white placeholder:text-slate-600 focus:outline-none"
                />
              </div>
              <button
                data-testid="product-search-submit"
                disabled={loading}
                className="glow-gold flex h-12 items-center justify-center gap-2 rounded-xl bg-amber-500 px-6 font-semibold text-black transition-colors duration-200 hover:bg-amber-400 disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                Confronta e risparmia
              </button>
            </form>
            {loading && <p data-testid="search-loading" className="mt-4 text-sm text-amber-300/90">{STEPS[step]} <span className="text-slate-500">(può richiedere fino a un minuto)</span></p>}
            <div className="mt-8 flex flex-wrap gap-6 text-sm text-slate-400">
              <span><b className="text-white">1 pt</b> per ogni € (compri tu)</span>
              <span><b className="text-white">2 pt</b> per ogni € (Fatelo voi)</span>
              <span><b className="text-white">Bronze · Gold · Diamond</b></span>
            </div>
          </div>
          <div className="relative hidden lg:block">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/8a990b7d-87bf-4959-9ab4-9392f63d36b3/images/56fdd763cb546969e4ae5477ded8e26d664069c17f330fb9cd98ac7963395892.jpeg"
              alt="Tessere Bronze, Gold e Diamond"
              className="h-full max-h-[460px] w-full rounded-3xl border border-white/10 object-cover opacity-90"
            />
            <div className="absolute -bottom-6 -left-6 rounded-2xl border border-amber-500/30 bg-[#12141C]/95 p-5 backdrop-blur">
              <p className="text-xs uppercase tracking-widest text-slate-400">Risparmio medio</p>
              <p className="font-display text-3xl font-bold text-emerald-400">fino al 30%</p>
            </div>
          </div>
        </div>
      </section>
      {result && <SearchResults result={result} />}
    </div>
  );
}
